// The org's student-system settings: whether the school uses classes at all, what
// consent a student needs before practising, how students practise (typing, voice), which
// content teachers may assign and how long student data is kept. Each control saves as it
// changes, like the join-request settings.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createLogger } from '../../utils/logger';
import { uiLocale } from '../../i18n/language';
import {
  ConsentType,
  RETENTION_DAYS_MAX,
  RETENTION_DAYS_MIN,
  StudentSystemSettings,
  StudentSystemSettingsInput,
  fetchStudentSystemSettings,
  updateStudentSystemSettings,
} from '../../services/classService';
import { cardStyle, errorMessage, errorStyle, hintStyle, inputStyle, labelStyle, noticeStyle, sectionHeadingStyle } from './classesStyles';

const logger = createLogger('StudentSystemSettingsCard');

const CONSENT_TYPES: ConsentType[] = ['guardian', 'school', 'student'];

/** A number that saves when the field loses focus (not per keystroke), and only when it
 *  is a whole number in range. An `optional` one may also be emptied, which saves null. */
const DefaultNumber: React.FC<{
  id: string;
  label: string;
  value: number | null;
  min?: number;
  max: number;
  disabled: boolean;
  optional?: boolean;
  placeholder?: string;
  onSave: (value: number | null) => void;
}> = ({ id, label, value, min = 1, max, disabled, optional, placeholder, onSave }) => {
  const shown = value === null ? '' : String(value);
  const [draft, setDraft] = useState(shown);
  // Follow the saved value when it changes (a save, a reload). Done during render, not in
  // an effect: a mount effect still pending when the first keystroke lands would put the
  // saved value back over it.
  const [lastShown, setLastShown] = useState(shown);
  if (shown !== lastShown) {
    setLastShown(shown);
    setDraft(shown);
  }
  const commit = () => {
    if (optional && !draft.trim()) {
      if (value !== null) onSave(null);
      return;
    }
    const next = Number(draft);
    if (Number.isInteger(next) && next >= min && next <= max && next !== value) onSave(next);
    else setDraft(shown);
  };
  return (
    <div>
      <label htmlFor={id} style={labelStyle}>
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={min}
        max={max}
        style={{ ...inputStyle, width: 90 }}
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
      />
    </div>
  );
};

interface Props {
  /** Omitted for an org admin, whose own org the server uses. */
  organizationId?: string | null;
  onChange?: (settings: StudentSystemSettings) => void;
}

const StudentSystemSettingsCard: React.FC<Props> = ({ organizationId, onChange }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<StudentSystemSettings | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const loaded = await fetchStudentSystemSettings(organizationId);
      setSettings(loaded);
      onChange?.(loaded);
    } catch (err) {
      logger.error('Failed to load student system settings', err);
      setError(t('classes.couldNotLoadSettings'));
    }
    // onChange is a notification, not an input: re-running on a new callback identity
    // would refetch on every parent render.
  }, [organizationId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (input: StudentSystemSettingsInput) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await updateStudentSystemSettings(input, organizationId);
      setSettings(updated);
      onChange?.(updated);
      setNotice(t('classes.settingsSaved'));
    } catch (err) {
      logger.error('Failed to save student system settings', err);
      setError(errorMessage(err, t('classes.couldNotSaveSettings')));
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return error ? <div style={errorStyle} role="alert">{error}</div> : null;
  }

  return (
    <section style={cardStyle} aria-labelledby="student-system-settings-heading">
      <h2 id="student-system-settings-heading" style={sectionHeadingStyle}>
        {t('classes.settingsTitle')}
      </h2>
      {error && <div style={errorStyle} role="alert">{error}</div>}
      {notice && <div style={noticeStyle} role="status">{notice}</div>}

      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
        <input
          type="checkbox"
          checked={settings.student_system_enabled}
          disabled={saving}
          onChange={e => save({ student_system_enabled: e.target.checked })}
        />
        {t('classes.studentSystemEnabled')}
      </label>
      <p style={hintStyle}>{t('classes.studentSystemEnabledHint')}</p>

      {settings.student_system_enabled && (
        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem' }}>
            <input
              type="checkbox"
              checked={settings.student_consent_required}
              disabled={saving}
              onChange={e => save({ student_consent_required: e.target.checked })}
            />
            {t('classes.consentRequired')}
          </label>
          <p style={hintStyle}>{t('classes.consentRequiredHint')}</p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: '1rem' }}>
            <DefaultNumber
              id="student-default-attempts"
              label={t('classes.defaultAttempts')}
              value={settings.student_default_max_attempts}
              max={50}
              disabled={saving}
              onSave={value => value !== null && save({ student_default_max_attempts: value })}
            />
            <DefaultNumber
              id="student-default-minutes"
              label={t('classes.defaultMinutes')}
              value={settings.student_default_session_minutes}
              max={15}
              disabled={saving}
              optional
              placeholder={t('classAssignments.minutesAuto')}
              onSave={value => save({ student_default_session_minutes: value })}
            />
          </div>
          <p style={hintStyle}>{t('classes.defaultsHint')}</p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginTop: '1rem' }}>
            <input
              type="checkbox"
              checked={settings.student_text_only}
              disabled={saving}
              onChange={e => save({ student_text_only: e.target.checked })}
            />
            {t('classes.textOnly')}
          </label>
          <p style={hintStyle}>{t('classes.textOnlyHint')}</p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginTop: '0.75rem' }}>
            <input
              type="checkbox"
              checked={settings.student_audio_recording_enabled && !settings.student_text_only}
              disabled={saving || settings.student_text_only}
              onChange={e => save({ student_audio_recording_enabled: e.target.checked })}
            />
            {t('classes.audioRecording')}
          </label>
          <p style={hintStyle}>{t('classes.audioRecordingHint')}</p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginTop: '0.75rem' }}>
            <input
              type="checkbox"
              checked={settings.student_ai_feedback_before_release ?? true}
              disabled={saving}
              onChange={e => save({ student_ai_feedback_before_release: e.target.checked })}
            />
            {t('classes.aiFeedbackBeforeRelease')}
          </label>
          <p style={hintStyle}>{t('classes.aiFeedbackBeforeReleaseHint')}</p>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', marginTop: '0.75rem' }}>
            <input
              type="checkbox"
              checked={settings.student_content_approval_required ?? true}
              disabled={saving}
              onChange={e => save({ student_content_approval_required: e.target.checked })}
            />
            {t('classes.contentApprovalRequired')}
          </label>
          <p style={hintStyle}>{t('classes.contentApprovalRequiredHint')}</p>

          <div style={{ marginTop: '1rem' }}>
            <DefaultNumber
              id="student-retention-days"
              label={t('classes.retentionDays')}
              value={settings.student_data_retention_days ?? 365}
              min={RETENTION_DAYS_MIN}
              max={RETENTION_DAYS_MAX}
              disabled={saving}
              onSave={value => value !== null && save({ student_data_retention_days: value })}
            />
            <p style={hintStyle}>
              {t('classes.retentionDaysHint', { min: RETENTION_DAYS_MIN, max: RETENTION_DAYS_MAX })}
            </p>
            {settings.student_data_retention_grace_until &&
              new Date(settings.student_data_retention_grace_until) > new Date() && (
                <p style={hintStyle}>
                  {t('classes.retentionGrace', {
                    date: new Date(settings.student_data_retention_grace_until).toLocaleDateString(uiLocale()),
                  })}
                </p>
              )}
          </div>

          {settings.student_consent_required && (
            <div style={{ marginTop: '0.75rem' }}>
              <label htmlFor="student-consent-type" style={labelStyle}>
                {t('classes.consentType')}
              </label>
              <select
                id="student-consent-type"
                style={inputStyle}
                value={settings.student_consent_type}
                disabled={saving}
                onChange={e => save({ student_consent_type: e.target.value as ConsentType })}
              >
                {CONSENT_TYPES.map(type => (
                  <option key={type} value={type}>
                    {t(`classes.consentTypes.${type}`)}
                  </option>
                ))}
              </select>
              <p style={hintStyle}>{t(`classes.consentTypeHints.${settings.student_consent_type}`)}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default StudentSystemSettingsCard;
